import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import { ColumnDef } from '@tanstack/react-table';
import { Box, Typography, useTheme } from '@mui/material';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import DateRangePicker from 'components/Common/DateRangePicker';
import { formatDate, titleCase } from 'utils/helperFunctions';
import { useGetBusinessAuditTrails } from '@/apis/business/setting/auditTrails/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';

interface FilterFormData {
  action?: string;
  dateRange?: string;
}

const defaultFormValues: FilterFormData = {
  action: ' ',
  dateRange: ''
};

export default function AuditTrails() {
  const theme = useTheme();
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [searchText, setSearchText] = useState<null | string>(null);
  const [filterSearchText, setFilterSearchText] = useState<null | string>(null);

  const transformFilters = (formData: FilterFormData) => {
    const filters: Record<string, any> = {};
    if (formData.action && formData.action !== 'all') {
      filters['filter[action_eq]'] = formData.action;
    }
    if (formData.dateRange) {
      filters['filter[created_at_in]'] = formData.dateRange;
    }
    return filters;
  };

  const watchedValues = watch();
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>(transformFilters(defaultFormValues));

  useEffect(() => {
    const transformedFilters = transformFilters(watchedValues);
    setAppliedFilters((prevFilters) => {
      const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(transformedFilters);
      if (filtersChanged) {
        resetPage();
      }
      return transformedFilters;
    });
  }, [watchedValues, resetPage]);

  useEffect(() => {
    setFilterSearchText(searchText);
    resetPage();
  }, [searchText, resetPage]);

  const { data: businessProfile } = useGetBusinessProfile();

  const { data: auditLogs, isLoading } = useGetBusinessAuditTrails({
    ...appliedFilters,
    'filter[name_cont]': filterSearchText,
    page: currentPage,
    per_page: perPage
  });

  return (
    <>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        mb={{ xs: 2, lg: 3 }}
        gap={{ xs: 2, lg: 0 }}
      >
        <Typography variant='h6' fontWeight={500}>
          Audit Trails
        </Typography>
        <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }} gap={1} width={{ xs: '100%', lg: 'auto' }}>
          <Box width={{ xs: '100%', lg: '270px' }}>
            <DateRangePicker name='dateRange' control={control} createdAt={businessProfile?.created_at} />
          </Box>
          <Box width={{ xs: '100%', lg: '170px' }}>
            <Select
              name='action'
              label='Action'
              defaultValue='all'
              control={control}
              options={[
                { value: ' ', label: 'All' },
                { value: '0', label: 'Logged in' },
                { value: 2, label: 'Agency Checked in' },
                { value: 1, label: 'Business Checked in' },
                { value: 4, label: 'Created' },
                { value: 3, label: 'Deleted' },
                { value: 5, label: 'Updated' },
                { value: 6, label: 'Business Reset' },
                { value: 7, label: 'Business Status' },
                { value: 25, label: 'Number Purchased' },
                { value: 26, label: 'Number Released' },
                { value: 27, label: 'SIP Created' },
                { value: 28, label: 'SIP Deleted' }
              ]}
              sx={{ height: '40px', background: theme.palette.background.paper }}
            />
          </Box>
          <Box width={{ xs: '100%', lg: '250px' }}>
            <Search
              size='small'
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder='Search audit trails...'
              fullWidth
            />
          </Box>
        </Box>
      </Box>

      <DataTable data={auditLogs?.data} columns={columns()} isHeaderSticky={true} isLoading={isLoading} />
      <Pagination
        totalPages={auditLogs?.meta?.total_pages}
        totalCount={auditLogs?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
    </>
  );
}

const columns = (): ColumnDef<any>[] => [
  {
    header: 'TIMESTAMP',
    cell: (info) => <Typography>{formatDate(info.row.original.created_at)}</Typography>
  },
  {
    header: 'USER',
    accessorKey: 'name'
  },
  {
    header: 'ACTION',
    cell: (info) => {
      const action = info.row.original.action;
      const colorMap: Record<string, { bg: string; color: string }> = {
        login: { bg: '#EEF5FF', color: '#1E40AF' },
        agency_checkin: { bg: '#D2ECFF', color: '#195EFF' },
        business_checkin: { bg: '#FFF3E5', color: '#FB8703' },
        create: { bg: '#F5DFFF', color: '#7C00B1' },
        delete: { bg: '#FFE5E5', color: '#DA0808' },
        business_reset: { bg: '#FFE5E5', color: '#DA0808' },
        business_status: { bg: '#D2ECFF', color: '#195EFF' },
        phone_number_purchased: { bg: '#DBFCDE', color: '#077F13' },
        phone_number_released: { bg: '#FFE5E5', color: '#DA0808' },
        sip_registration_created: { bg: '#F5DFFF', color: '#7C00B1' },
        sip_registration_deleted: { bg: '#FFE5E5', color: '#DA0808' }
      };
      const style = colorMap[action] || { bg: '#F3F4F6', color: '#374151' };

      return (
        <Typography
          sx={{
            px: 1.5,
            py: 0.5,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            bgcolor: style.bg,
            color: style.color,
            textTransform: 'capitalize',
            display: 'inline-block',
            textWrap: 'nowrap'
          }}
        >
          {titleCase(action)}
        </Typography>
      );
    }
  },
  {
    header: 'TARGET',
    accessorKey: 'target'
  },
  {
    header: 'STATUS',
    cell: (info) => {
      const status = info.row.original.status;
      const isSuccess = status?.toLowerCase() === 'success';
      return (
        <Typography
          sx={{
            px: 1.5,
            py: 0.5,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            bgcolor: isSuccess ? '#DBFCDE' : '#FFE5E5',
            color: isSuccess ? '#077F13' : '#DA0808',
            display: 'inline-block'
          }}
        >
          {titleCase(status)}
        </Typography>
      );
    }
  },
  {
    header: 'DETAILS',
    accessorKey: 'description'
  }
];
